import { createRouter, createWebHistory } from 'vue-router'
import { isAuthenticated, isApiConfigured, loadServerConfig, isDevelopment } from '@/api/config'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    const isSamePathNavigation = to.path === from.path
    const isContactFocusNavigation = to.name === 'account-detail'
      && (to.query.focusContact === '1'
        || to.query.focusContactKey != null
        || to.query.openContact != null
        || to.query.contactName != null
        || to.query.matchedValue != null)

    if (isSamePathNavigation || isContactFocusNavigation) {
      return false
    }

    return { top: 0 }
  },
  routes: [
    // ── Public ──────────────────────────────────────────────────────────────
    {
      path: '/',
      redirect: to => ({ path: '/login', query: to.query })
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue')
    },
    {
      path: '/no-tenant',
      name: 'no-tenant',
      component: () => import('@/views/NoTenantView.vue')
    },

    // ── Protected (requires sales rep session) ────────────────────────────
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/proofs',
      name: 'proofs',
      component: () => import('@/views/ProofsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/proofs/new-ad/:orderImageKey',
      name: 'new-ad-detail',
      component: () => import('@/views/NewAdDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/proofs/:proofKey',
      name: 'proof-detail',
      component: () => import('@/views/ProofDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/accounts',
      name: 'accounts',
      component: () => import('@/views/AccountsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/accounts/:advertiserKey/pay',
      name: 'account-payment',
      component: () => import('@/views/PaymentView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/accounts/:advertiserKey',
      name: 'account-detail',
      component: () => import('@/views/AccountDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/orders/new/:advertiserKey?',
      name: 'new-order',
      component: () => import('@/views/NewOrderView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/deals',
      name: 'deals',
      component: () => import('@/views/DealsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/deals/new',
      name: 'deal-new',
      component: () => import('@/views/DealDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/deals/:dealKey',
      name: 'deal-detail',
      component: () => import('@/views/DealDetailView.vue'),
      meta: { requiresAuth: true }
    },

    // ── Admin ────────────────────────────────────────────────────────────
    {
      path: '/admin',
      name: 'admin-login',
      component: () => import('@/views/admin/AdminLoginView.vue'),
      meta: { isAdmin: true }
    },
    {
      path: '/admin/tenants',
      name: 'admin-tenants',
      component: () => import('@/views/admin/AdminTenantsView.vue'),
      meta: { isAdmin: true, requiresAdminAuth: true }
    },
    {
      path: '/admin/tenants/new',
      name: 'admin-tenant-new',
      component: () => import('@/views/admin/AdminTenantEditView.vue'),
      meta: { isAdmin: true, requiresAdminAuth: true }
    },
    {
      path: '/admin/tenants/:id',
      name: 'admin-tenant-edit',
      component: () => import('@/views/admin/AdminTenantEditView.vue'),
      meta: { isAdmin: true, requiresAdminAuth: true }
    }
  ]
})

let configLoaded = false

router.beforeEach(async (to, _from, next) => {
  // Admin routes bypass tenant config loading
  if (to.meta.isAdmin) {
    if (to.meta.requiresAdminAuth) {
      const adminToken = sessionStorage.getItem('salesRepApp_adminToken')
      if (!adminToken) {
        return next({ name: 'admin-login' })
      }
    }
    return next()
  }

  // Load tenant config once on first navigation
  if (!configLoaded) {
    configLoaded = true
    await loadServerConfig()
  }

  // Redirect to no-tenant page if API not configured (and not dev mode)
  if (!isApiConfigured() && !isDevelopment()) {
    if (to.name !== 'no-tenant') {
      return next({ name: 'no-tenant' })
    }
    return next()
  }

  // Auth guard — in dev mode, try auto-login before redirecting
  if (to.meta.requiresAuth && !isAuthenticated()) {
    if (isDevelopment()) {
      const authStore = useAuthStore()
      const ok = await authStore.tryDevAutoLogin()
      if (ok) return next()
    }
    return next({ name: 'login', query: to.query })
  }

  // Redirect logged-in users away from login
  if (to.name === 'login' && isAuthenticated()) {
    return next({ name: 'dashboard' })
  }

  next()
})

export default router
